import styled from '@emotion/styled';
import { Box, Link as MuiLink } from '@mui/material';
import React from 'react';
import { Palette } from 'Palette';
import { CARD_LINK_BUTTON } from 'DesignTokens';

const Wrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'hasArrow',
})<{ hasArrow?: boolean }>(({ hasArrow }) => ({
  width: CARD_LINK_BUTTON.width,
  minWidth: CARD_LINK_BUTTON.minWidth,
  height: CARD_LINK_BUTTON.height,
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: hasArrow ? 'space-between' : CARD_LINK_BUTTON.justifyContentNoArrow,
  gap: '0.5rem',
  borderBottom: CARD_LINK_BUTTON.borderBottom ?? `2px solid ${Palette.default.text}`,
  textDecoration: 'none',
  backgroundColor: CARD_LINK_BUTTON.background,
  borderRadius: CARD_LINK_BUTTON.borderRadius,
  padding: CARD_LINK_BUTTON.padding,
  cursor: 'pointer',
  transition: CARD_LINK_BUTTON.hoverBackground ? 'background-color 0.15s ease' : undefined,
  '&:hover': CARD_LINK_BUTTON.hoverBackground
    ? { backgroundColor: CARD_LINK_BUTTON.hoverBackground }
    : undefined,
}));

const Text = styled('span')({
  fontFamily: CARD_LINK_BUTTON.fontFamily,
  fontWeight: CARD_LINK_BUTTON.fontWeight,
  fontSize: '1rem',
  textTransform: CARD_LINK_BUTTON.textTransform,
  color: CARD_LINK_BUTTON.color ?? Palette.default.text,
  letterSpacing: CARD_LINK_BUTTON.letterSpacing,
  whiteSpace: 'nowrap',
});

export const ArrowIcon = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  svg: {
    stroke: CARD_LINK_BUTTON.color ?? '#13504B',
    fill: 'none',
  },
});

export const StyledSvg = () => (
  <svg width="16" height="16" viewBox="0 0 24 24">
    <path
      d="M5 12h14M13 6l6 6-6 6"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface StyledCardLinkProps {
  text: string;
  url?: string;
  onClick?: () => void;
  padding?: number;
  showArrow?: boolean;
}

const StyledCardLink: React.FC<StyledCardLinkProps> = ({
  text,
  url,
  onClick,
  padding = 0,
  showArrow = true,
}) => {
  return (
    <Box sx={{ px: padding, pb: padding }}>
      {url ? (
        <MuiLink href={url} underline="none" sx={{ width: '100%', display: 'block' }}>
          <Wrapper hasArrow={showArrow}>
            <Text>{text}</Text>
            {showArrow && (
              <ArrowIcon>
                <StyledSvg />
              </ArrowIcon>
            )}
          </Wrapper>
        </MuiLink>
      ) : onClick ? (
        <Wrapper hasArrow={showArrow} onClick={onClick} role="button">
          <Text style={CARD_LINK_BUTTON.textNudge ? { paddingLeft: '0.3125rem' } : undefined}>
            {text}
          </Text>
          {showArrow && (
            <ArrowIcon
              style={CARD_LINK_BUTTON.textNudge ? { paddingRight: '0.3125rem' } : undefined}
            >
              <StyledSvg />
            </ArrowIcon>
          )}
        </Wrapper>
      ) : null}
    </Box>
  );
};

export default StyledCardLink;
